import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Subscription } from 'rxjs';

import { FinaPayment, FinaPaymentClaimedEvent, FinaPaymentConfirmedEvent } from '../models/fina-payments.model';
import { FinaPaymentsService } from './fina-payments.service';
import { SocketService } from './socket.service';
import { ToastService } from './toast.service';

/**
 * Fina Pago · cobros entrantes por el QR fijo del comercio. Un cliente escanea el QR impreso, paga,
 * y el cobro llega sin venta: esta bandeja lo pone a la vista del cajero para que lo aplique.
 *
 * Mismo patrón que `OnboardingService.initializeRealTimeUpdates()`: `connect()` (idempotente) y
 * `on()` en el mismo método (si `on()` va antes de `connect()`, el observable completa en el acto),
 * resync por HTTP en `reconnect$`. El socket solo avisa; la fuente de verdad es `GET /charges?unassigned=1`,
 * y con el POS abierto se re-sincroniza cada 30 s por si el socket no llega (varias réplicas).
 */
@Injectable({
    providedIn: 'root',
})
export class FinaPaymentsIncomingService {
    private readonly _unassigned$ = new BehaviorSubject<FinaPayment[]>([]);
    /** Cobros QR pagados y sin aplicar (los reservados por otra caja no vienen del api). */
    readonly unassigned$ = this._unassigned$.asObservable();

    private subs = new Subscription();
    private pollSub: Subscription | null = null;
    private started = false;

    constructor(private socketService: SocketService, private finaPaymentsService: FinaPaymentsService, private toastService: ToastService) {}

    /** Llamar tras login / auto-login (AuthService). Idempotente. */
    start(): void {
        if (this.started) return;
        this.started = true;
        this.socketService.connect();

        this.subs.add(
            this.socketService.on<FinaPaymentConfirmedEvent>('payment_confirmed').subscribe(ev => {
                // Los cobros del modal (con venta) los sigue el propio modal por `paymentId`.
                if (!ev || (ev.saleId !== null && ev.saleId !== undefined)) return;
                try {
                    this.resync();
                    const amount = Number(ev.amountVES || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    this.toastService.info('Fina Pago', `Cobro por QR recibido · Bs. ${amount}`);
                } catch (err) {
                    console.error('[FinaPaymentsIncoming] payment_confirmed:', err);
                }
            }),
        );
        this.subs.add(
            this.socketService.on<FinaPaymentClaimedEvent>('payment_claimed').subscribe(ev => {
                if (!ev?.paymentId) return;
                this.remove(ev.paymentId);
            }),
        );
        this.subs.add(this.socketService.reconnect$.subscribe(() => this.resync()));
    }

    /** El POS lo llama al abrir el panel de cobro: carga inicial y resync cada 30 s. */
    attachPos(): void {
        this.resync();
        if (!this.pollSub) this.pollSub = interval(30_000).subscribe(() => this.resync());
    }

    detachPos(): void {
        this.pollSub?.unsubscribe();
        this.pollSub = null;
    }

    resync(): void {
        if (!this.finaPaymentsService.isActive) return;
        this.finaPaymentsService.getUnassignedCharges$().subscribe({
            next: r => this._unassigned$.next(r?.payments || []),
            error: () => {
                /* silencioso: el siguiente tick o evento lo reintenta */
            },
        });
    }

    /** Quita un cobro de la bandeja local (aplicado aquí o reservado por otra caja). */
    remove(paymentId: string): void {
        this._unassigned$.next(this._unassigned$.value.filter(p => p.paymentId !== paymentId));
    }

    /** Al cerrar sesión: sin suscripciones vivas ni cobros de un tenant anterior. */
    stop(): void {
        this.detachPos();
        this.subs.unsubscribe();
        this.subs = new Subscription();
        this._unassigned$.next([]);
        this.started = false;
    }
}
